import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Clock, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';
import { fmtDate } from '../../lib/dates';

const label = (h) => (h === 168 ? 'Semanal' : `Cada ${h} horas`);

export const SourceStamp = ({ ok, fail, error, testid }) => (
  <div data-testid={testid} className="flex flex-col gap-0.5 text-xs">
    <span className="flex items-center gap-1 text-emerald-300"><CheckCircle2 className="h-3 w-3" />Última consulta exitosa: {ok ? fmtDate(ok, true) : 'nunca'}</span>
    {fail && (!ok || fail > ok) && <span className="flex items-start gap-1 text-rose-300"><XCircle className="mt-0.5 h-3 w-3 shrink-0" />Último intento fallido: {fmtDate(fail, true)}{error ? ` · ${error}` : ''}</span>}
  </div>
);

// Review frequency (editable), real scheduler state and the Diario Oficial / manual check controls
export const WatchStatusBar = ({ data, onChange }) => {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const s = data.settings;
  const setFreq = async (h) => { try { await api.put('/watched-laws/settings', { interval_hours: Number(h) }); toast.success('Frecuencia actualizada'); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); } };
  const now = async () => {
    setBusy(true);
    try { await runAiJob(api, '/watched-laws/check-now', {}, { timeout: 600000, interval: 3000 }); toast.success('Revisión terminada'); onChange(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const d = data.diario_oficial;
  return (
    <div data-testid="watch-status-bar" className="grid gap-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-5 md:grid-cols-3">
      <div className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-500">Frecuencia de revisión</span>
        <select data-testid="watch-frequency" value={s.interval_hours} onChange={(e) => setFreq(e.target.value)} className="w-fit rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm">
          {data.intervals.map((h) => <option key={h} value={h}>{label(h)}</option>)}
        </select>
        <span className="flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3 w-3" />Última: {s.last_run_at ? fmtDate(s.last_run_at, true) : 'aún no'} · Próxima: {fmtDate(s.next_run_at, true)}</span>
        <span data-testid="watch-scheduler" className={`text-xs ${data.scheduler.active ? 'text-emerald-300' : 'text-amber-300'}`}>
          {data.scheduler.active ? `Proceso programado activo (último ciclo ${fmtDate(data.scheduler.last_slot, true)})` : 'Proceso programado sin ejecución comprobada en la última hora: la revisión automática no está garantizada.'}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-500">Diario Oficial (lectura directa)</span>
        <SourceStamp testid="watch-do-status" ok={d.last_ok_at} fail={d.last_fail_at} error={d.last_error} />
        <span className="text-[11px] text-slate-500">Si la lectura directa falla, las publicaciones se cruzan con el registro de normas publicadas de BCN, que informa la fecha de publicación en el Diario Oficial.</span>
      </div>
      <div className="flex flex-col items-start gap-2 md:items-end">
        <button data-testid="watch-check-now" disabled={busy} onClick={now} className="flex items-center gap-1.5 rounded-full border border-amber-500/60 px-4 py-2 text-sm text-amber-200 hover:bg-amber-500/10 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}{busy ? 'Revisando en BCN…' : 'Revisar ahora'}
        </button>
      </div>
    </div>
  );
};
